import * as React from "react";
import l10n from '../../util/l10n';

import BoldIcon from 'material-ui/svg-icons/editor/format-bold';
import ItalicIcon from 'material-ui/svg-icons/editor/format-italic';
import UnderlinedIcon from 'material-ui/svg-icons/editor/format-underlined';
import StrikethroughIcon from 'material-ui/svg-icons/editor/format-strikethrough';

import AlignLeftIcon from 'material-ui/svg-icons/editor/format-align-left';
import AlignCenterIcon from 'material-ui/svg-icons/editor/format-align-center';
import AlignRightIcon from 'material-ui/svg-icons/editor/format-align-right';
import AlignJustifyIcon from 'material-ui/svg-icons/editor/format-align-justify';

import ListNumberedIcon from 'material-ui/svg-icons/editor/format-list-numbered';
import ListBulletedIcon from 'material-ui/svg-icons/editor/format-list-bulleted';

import LinkIcon from 'material-ui/svg-icons/editor/insert-link';
import UnlinkIcon from 'material-ui/svg-icons/navigation/close';

import IndentDecreaseIcon from 'material-ui/svg-icons/editor/format-indent-decrease';
import IndentIncreaseIcon from 'material-ui/svg-icons/editor/format-indent-increase';

import ClearIcon from 'material-ui/svg-icons/editor/format-clear';

import ContactsIcon from 'material-ui/svg-icons/communication/contacts';

import PaletteIcon from 'material-ui/svg-icons/image/palette';
import PasteIcon from 'material-ui/svg-icons/content/content-paste';
import CodeIcon from 'material-ui/svg-icons/action/code';

import KeyboardArrowDownIcon from 'material-ui/svg-icons/hardware/keyboard-arrow-down';

import { PasteFromWordIcon } from '../../custom-components/svg-icons/PasteFromWord';
import { Subscript } from '../../custom-components/svg-icons/Subscript';
import { Superscript } from '../../custom-components/svg-icons/Superscript';
import { AddEditTable } from '../../custom-components/svg-icons/table/AddEditTable';
import { RowInsertBefore } from '../../custom-components/svg-icons/table/insert/RowInsertBefore';
import { RowInsertAfter } from '../../custom-components/svg-icons/table/insert/RowInsertAfter';
import { ColumnInsertBefore } from '../../custom-components/svg-icons/table/insert/ColumnInsertBefore';
import { ColumnInsertAfter } from '../../custom-components/svg-icons/table/insert/ColumnInsertAfter';
import { RowDelete } from '../../custom-components/svg-icons/table/remove/RowDelete';
import { ColumnDelete } from '../../custom-components/svg-icons/table/remove/ColumnDelete';
import { CellMerge } from '../../custom-components/svg-icons/table/merge/CellMerge';
import { CellMergeDown } from '../../custom-components/svg-icons/table/merge/CellMergeDown';
import { CellMergeRight } from '../../custom-components/svg-icons/table/merge/CellMergeRight';
import { CellHorizontalSplit } from '../../custom-components/svg-icons/table/split/CellHorizontalSplit';
import { CellVerticalSplit } from '../../custom-components/svg-icons/table/split/CellVerticalSplit';

import CKEditorFontSizeMenu from '../../custom-components/ckeditor/CKEditorFontSizeMenu';
import CKEditorFormatMenu from '../../custom-components/ckeditor/CKEditorFormatMenu';
import  { ColorPickerCKEditorDropdown } from '../../components/color-picker/ColorPickerCKEditorDropdown';

import Modul from '../../module/Modul';
import OpenGeneralStyles from '../OpenGeneralStyles';

export interface ButtonConfig {
    Icon: typeof __MaterialUI.SvgIcon;
    text: string;
    command: string;
    AdditionalIcon?: typeof __MaterialUI.SvgIcon;
    commandData?(event: React.SyntheticEvent<{}>): any;
}

export interface ItemFactory {
    (ckEditor: CKEditor): React.ReactElement<any>;
}

export type ItemConfig = ButtonConfig | ItemFactory;

export const allCKEditorItems = {
    Bold: {
        Icon: BoldIcon,
        text: l10n('module.text.style.bold'),
        command: 'bold'
    },
    Italic: {
        Icon: ItalicIcon,
        text: l10n('module.text.style.italic'),
        command: 'italic'
    },
    Underline: {
        Icon: UnderlinedIcon,
        text: l10n('module.text.style.underline'),
        command: 'underline'
    },
    Strike: {
        Icon: StrikethroughIcon,
        text: l10n('module.text.style.strikethrough'),
        command: 'strike'
    },
    Superscript: {
        Icon: Superscript,
        text: l10n('module.text.style.superscript'),
        command: 'superscript'
    },
    Subscript: {
        Icon: Subscript,
        text: l10n('module.text.style.subscript'),
        command: 'subscript'
    },
    JustifyLeft: {
        Icon: AlignLeftIcon,
        text: l10n('module.text.align.left'),
        command: 'justifyleft'
    },
    JustifyCenter: {
        Icon: AlignCenterIcon,
        text: l10n('module.text.align.center'),
        command: 'justifycenter'
    },
    JustifyRight: {
        Icon: AlignRightIcon,
        text: l10n('module.text.align.right'),
        command: 'justifyright'
    },
    JustifyBlock: {
        Icon: AlignJustifyIcon,
        text: l10n('module.text.align.justify'),
        command: 'justifyblock'
    },
    NumberedList: {
        Icon: ListNumberedIcon,
        text: l10n('module.text.list.numbered'),
        command: 'numberedlist'
    },
    BulletedList: {
        Icon: ListBulletedIcon,
        text: l10n('module.text.list.bulleted'),
        command: 'bulletedlist'
    },
    Link: {
        Icon: LinkIcon,
        AdditionalIcon: KeyboardArrowDownIcon,
        text: l10n('module.text.link.add'),
        command: 'diylink',
        commandData(event: React.SyntheticEvent<{}>) {
            return {
                anchorTo: event.target,
                zIndex: 101001,
                offset: '0 4'
            };
        }
    },
    Unlink: {
        Icon: UnlinkIcon,
        text: l10n('module.text.link.remove'),
        command: 'unlink'
    },
    Outdent: {
        Icon: IndentDecreaseIcon,
        text: l10n('module.text.indent.decrease'),
        command: 'outdent'
    },
    Indent: {
        Icon: IndentIncreaseIcon,
        text: l10n('module.text.indent.increase'),
        command: 'indent'
    },
    RemoveFormat: {
        Icon: ClearIcon,
        text: l10n('module.text.style.clear'),
        command: 'removeFormat'
    },
    ContactInfo: {
        Icon: ContactsIcon,
        AdditionalIcon: KeyboardArrowDownIcon,
        text: l10n('module.text.contactinfo'),
        command: 'togglesemanticplaceholderflyout',
        commandData(event: React.SyntheticEvent<{}>) {
            return {
                anchorTo: event.target,
                zIndex: 101001,
                offset: '0 4'
            };
        }
    },
    Paste: {
        Icon: PasteIcon,
        text: l10n('module.text.paste.text'),
        command: 'pastetext'
    },
    PasteFromWord: {
        Icon: PasteFromWordIcon,
        text: l10n('module.text.paste.word'),
        command: 'pastefromword'
    },
    SourceDialog: {
        Icon: CodeIcon,
        text: l10n('module.text.edithtml'),
        command: 'sourcedialog'
    },
    TableMixed: {
        Icon: AddEditTable,
        text: l10n('module.table.addEdit'),
        command: 'tableMixed'
    },
    RowInsertBefore: {
        Icon: RowInsertBefore,
        text: l10n('module.table.rowInsertBefore'),
        command: 'rowInsertBefore'
    },
    RowInsertAfter: {
        Icon: RowInsertAfter,
        text: l10n('module.table.rowInsertAfter'),
        command: 'rowInsertAfter'
    },
    ColumnInsertBefore: {
        Icon: ColumnInsertBefore,
        text: l10n('module.table.columnInsertBefore'),
        command: 'columnInsertBefore'
    },
    ColumnInsertAfter: {
        Icon: ColumnInsertAfter,
        text: l10n('module.table.columnInsertAfter'),
        command: 'columnInsertAfter'
    },
    RowDelete: {
        Icon: RowDelete,
        text: l10n('module.table.rowDelete'),
        command: 'rowDelete'
    },
    ColumnDelete: {
        Icon: ColumnDelete,
        text: l10n('module.table.columnDelete'),
        command: 'columnDelete'
    },
    CellMerge: {
        Icon: CellMerge,
        text: l10n('module.table.cellMerge'),
        command: 'cellMerge'
    },
     CellMergeDown: {
        Icon: CellMergeDown,
        text: l10n('module.table.cellMergeDown'),
        command: 'cellMergeDown'
    },
    CellMergeRight: {
        Icon: CellMergeRight,
        text: l10n('module.table.cellMergeRight'),
        command: 'cellMergeRight'
    },
    CellHorizontalSplit: {
        Icon: CellHorizontalSplit,
        text: l10n('module.table.cellHorizontalSplit'),
        command: 'cellHorizontalSplit'
    },
    CellVerticalSplit: {
        Icon: CellVerticalSplit,
        text: l10n('module.table.cellVerticalSplit'),
        command: 'cellVerticalSplit'
    },
    FontSize: (ckEditor: CKEditor) =>
        <CKEditorFontSizeMenu
            ckEditor={ckEditor}
            key="FontSize"
        />
    ,
    Format: (ckEditor: CKEditor) =>
        <CKEditorFormatMenu
            ckEditor={ckEditor}
            key="Format"
        />
    ,
    DiyTextColor: (ckEditor: CKEditor) =>
        <ColorPickerCKEditorDropdown
            ckEditor={ckEditor}
            onChange={(color: string) => {
                ckEditor.execCommand('diycolorapply', color);
            }}
            key="DiyTextColor"
        />
    ,
    TextStyles: (module: Modul) => (ckEditor: CKEditor) =>
        <OpenGeneralStyles
            module={module}
            Icon={PaletteIcon}
            label={l10n('module.text.generalstyles')}
            key="openStyleSection"
        />
};



/** WEBPACK FOOTER **
 ** ./build/js/~/tslint-loader!./source/cdn/document_root/s/diy/custom-components/ckeditor/AllCKEditorButtonsConfig.tsx
 **/